import React, { useState } from 'react';
import type { ButtonSize } from '@wix/design-system';
import { Box, Button, Loader } from '@wix/design-system';
import type { AppBadge } from '@wix/app-market-services';
import {
  useTranslation,
  useEssentials,
  useServices,
  useSiteContext,
  QueryDecorator,
} from '@wix/app-market-core';
import { useUpgradeButtonBiEvents } from './use-upgrade-bi-event';
import { hasBenefitOrBundleBadge } from '../../utils/badges';
import { IframeModal } from '../../../../components/iframe-modal';
import { getAppPackagePickerUrl } from './get-app-package-picker-url';

export const UPGRADE_APP_BUTTON_DATA_HOOK = 'upgrade-app-button';
const UPGRADE_APP_MODAL_EXPERIMENT = 'specs.app-market.UpgradeAppModal';

export interface UpgradeAppButtonProps {
  app: {
    id: string;
    instanceId?: string;
    appBadges: AppBadge[];
    externalPricingPageUrl?: string;
  };
  size: ButtonSize;
  origin: string;
}

export function UpgradeAppButton({ app, size, origin }: UpgradeAppButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const services = useServices();
  const { experiments } = useEssentials();
  const { t } = useTranslation();
  const { metaSiteId } = useSiteContext();

  const shouldUseModal =
    !app.externalPricingPageUrl &&
    experiments.enabled(UPGRADE_APP_MODAL_EXPERIMENT);

  const appPackagePickerUrl = getAppPackagePickerUrl({
    appId: app.id,
    metaSiteId: metaSiteId as string,
    isModalEnabled: shouldUseModal,
  });
  const showRedeemOfferButton = hasBenefitOrBundleBadge({
    experiments,
    appBadges: app.appBadges,
  });
  const { reportUpgradeClick, reportFreeTrialClick } = useUpgradeButtonBiEvents(
    {
      app,
      origin,
      showRedeemOfferButton,
    },
  );

  const handleUpgradeClick = () => {
    if (shouldUseModal) {
      setIsModalOpen(true);
    } else {
      window.open(appPackagePickerUrl, '_blank');
    }
  };

  return (
    <>
      <QueryDecorator
        queryFn={async () => {
          const freeTrialAppIds = await (metaSiteId
            ? services.getEligibleAppsForFreeTrial()
            : Promise.resolve([]));

          return freeTrialAppIds.includes(app.id);
        }}
        queryKey={`upgrade-app-button-${app.id}`}
        csrOnly
      >
        {({ data: isAppEligibleForFreeTrial, isLoading }) => {
          return (
            <Box direction="vertical" flexGrow={1}>
              <Button
                dataHook={UPGRADE_APP_BUTTON_DATA_HOOK}
                disabled={isLoading}
                skin={isAppEligibleForFreeTrial ? 'standard' : 'premium'}
                size={size}
                onClick={() => {
                  if (isAppEligibleForFreeTrial) {
                    reportFreeTrialClick();
                  } else {
                    reportUpgradeClick();
                  }
                  handleUpgradeClick();
                }}
              >
                {isLoading ? (
                  <Loader size="tiny" />
                ) : isAppEligibleForFreeTrial ? (
                  t('app.page.upgrade.cta.label.free.trial')
                ) : showRedeemOfferButton ? (
                  t('app.page.upgrade.cta.label.benefit')
                ) : (
                  t('app.page.upgrade.cta.label')
                )}
              </Button>
            </Box>
          );
        }}
      </QueryDecorator>
      {shouldUseModal && isModalOpen && (
        <IframeModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          url={appPackagePickerUrl}
          title="Upgrade App"
        />
      )}
    </>
  );
}
